<template>
  <div v-if="isLoading" class="text-center text-muted my-3">
    <div class="spinner-border spinner-border-sm" role="status">
      <span class="visually-hidden">Calculando...</span>
    </div>
    <span class="ms-2">Calculando parcelas...</span>
  </div>

  <div class="mb-3">
    <label class="form-label">Parcelas</label>
    <select 
      v-model="localSelectedInstallment" 
      class="form-select" 
      :disabled="disabled || isLoading || availableInstallments.length === 0"
    >
      <option :value="null" disabled>
        {{ placeholderText }}
      </option>
      <option v-for="p in availableInstallments" :key="p.number" :value="p">
        {{ p.number }}x de R$ {{ formatCurrency(p.installment_value) }}
        ({{ p.interest_rate === 0 ? 'sem juros' : 'com juros' }})
      </option>
    </select>
  </div>
  
  <div v-if="error && !isLoading && availableInstallments.length === 0" class="text-danger small mt-2">
    {{ error }}
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import api from '@/services/api';
import { formatCurrency } from '@/utils/currencyFormatter';

const props = defineProps({
  bin: { type: String, required: true },
  total: { type: Number, required: true },
  disabled: { type: Boolean, default: false },
  allowWithoutBin: { type: Boolean, default: false },
  initialSelectedInstallment: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['update:selectedInstallment', 'loading']);

const isLoading = ref(false);
const error = ref('');
const availableInstallments = ref([]);
const localSelectedInstallment = ref(null);
const lastCheckedBin = ref('');

const placeholderText = computed(() => {
  if (isLoading.value) {
    return 'Calculando parcelas...';
  } 
  if (error.value) {   
    return error.value;
  }  
  if (!props.allowWithoutBin && (!props.bin || props.bin.length < 6)) {
    return 'Digite o número do cartão para calcular as parcelas';
  } 
  
  if (availableInstallments.value.length === 0) {
    return 'Nenhuma opção de parcelamento disponível';
  }  
  return 'Selecione uma parcela';
});


function getCardBrand(bin) {  
  const cleanedBin = bin.replace(/\s/g, '');

  if (/^4/.test(cleanedBin)) {
    return "visa";
  }
  if (/^5[1-5]/.test(cleanedBin)) {
    return "mastercard";
  }
  if (/^3[47]/.test(cleanedBin)) {
    return "amex";
  }
  if (/^3(?:0[0-5]|[689])/.test(cleanedBin)) {
    return "dinersclub";
  }
  if (/^6(?:011|4[4-9]|5)/.test(cleanedBin)) {
    return "discover";
  }
  if (/^35(?:2[89]|[3-8][0-9])/.test(cleanedBin)) {
    return "jcb";
  }
  if (/^(401178|431274|438935|451416|457393|457631|504175|506699|5067[0-9]{2}|509[0-9]{3}|627780|636297|636368|65003[1-9]|6500[4-5][0-9]|6504[0-9]{2}|65090[1-2]|65165[0-9]|65500[0-9]|6550[1-2][0-9]|6550[5-9][0-9])/.test(cleanedBin)) {
    return "elo";
  }  
  if (/^(606282|3841|637095|637568|637599|637609|637612|637614)/.test(cleanedBin)) {
    return "hipercard";
  }
  
  return "";
}

function applyInitialSelection() {
  if (props.initialSelectedInstallment && availableInstallments.value.length > 0) {
    const found = availableInstallments.value.find(
      (inst) => inst.number === props.initialSelectedInstallment.number && 
                 inst.installment_value === props.initialSelectedInstallment.installment_value
    );
    if (found) {
      localSelectedInstallment.value = found;
      emit('update:selectedInstallment', found);
      return true;
    }
  }
  return false;
}

/**
 * aplicarFallback
 *
 * Usado quando o backend falha ou retorna lista vazia.
 * Garante que o usuário sempre tenha pelo menos 1x sem juros
 * disponível para prosseguir com o pagamento.
 */
function aplicarFallback() {
  const fallback = {
    number:            1,
    interest_rate:     0,
    installment_value: props.total,
    total_value:       props.total,
    description:       '1x sem juros',
  };
  availableInstallments.value = [fallback];
  localSelectedInstallment.value = fallback;
  emit('update:selectedInstallment', fallback);
}

watch(() => props.bin, async (newBin) => {
  if (props.allowWithoutBin) {
    if (availableInstallments.value.length > 0) return;

    isLoading.value = true;
    error.value = '';
    emit('loading', true);

    try {
      const payload = { total: props.total };
      const { data } = await api.post('/payments/installments', payload);

      availableInstallments.value = data.installments || [];

      const initialApplied = applyInitialSelection();
      if (!initialApplied) {
        const oneXNoInterest = availableInstallments.value.find(p => p.number === 1 && p.interest_rate === 0);
        const fallbackSelection = oneXNoInterest || availableInstallments.value[0] || null;

        if (fallbackSelection) {
          localSelectedInstallment.value = fallbackSelection;
          emit('update:selectedInstallment', fallbackSelection);
        } else {
          aplicarFallback();
        }
      }
    } catch (err) {
      console.error('Error fetching installments without BIN:', err);
      error.value = '';
      aplicarFallback();
    } finally {
      isLoading.value = false;
      emit('loading', false);
    }
    return;
  }

  if (newBin.length < 6) {
    availableInstallments.value = [];
    localSelectedInstallment.value = null;
    lastCheckedBin.value = '';
    error.value = '';
    emit('update:selectedInstallment', null);
    return;
  }

  if (newBin === lastCheckedBin.value) {
    return;
  }

  const brand = getCardBrand(newBin);
  if (!brand) {
    availableInstallments.value = [];
    localSelectedInstallment.value = null;
    error.value = "BIN não reconhecido ou inválido para cálculo de parcelas.";
    emit('update:selectedInstallment', null);
    return;
  }

  isLoading.value = true;
  error.value = '';
  lastCheckedBin.value = newBin;
  emit('loading', true);

  try {
    const payload = { brand, bin: newBin, total: props.total };
    const { data } = await api.post("/payments/installments", payload);
    
    availableInstallments.value = data.installments || []; 
    
    const initialApplied = applyInitialSelection();

    if (!initialApplied) {
      let defaultInstallmentToSelect = null;

      const oneXNoInterest = availableInstallments.value.find(p => p.number === 1 && p.interest_rate === 0);
      if (oneXNoInterest) {
        defaultInstallmentToSelect = oneXNoInterest;
      } 
      else if (availableInstallments.value.length > 0) {
        defaultInstallmentToSelect = availableInstallments.value[0];
      }

      if (defaultInstallmentToSelect) {
        localSelectedInstallment.value = defaultInstallmentToSelect;
        emit('update:selectedInstallment', defaultInstallmentToSelect);
      } else {
        aplicarFallback();
      }
    } 
    else if (availableInstallments.value.length === 0) {
        aplicarFallback();
    }

  } catch (err) {
    console.error("Error fetching installments:", err);
    error.value = '';
    aplicarFallback();
  } finally {
    isLoading.value = false;
    emit('loading', false);
  }
}, { immediate: true });

watch(() => props.initialSelectedInstallment, (newVal, oldVal) => {
  if (newVal !== oldVal || localSelectedInstallment.value === null) {
     applyInitialSelection();
  }
}, { deep: true });

watch(localSelectedInstallment, (newVal) => {
  emit('update:selectedInstallment', newVal);
});
</script>

<style scoped>
.form-select:disabled {
  background-color: #eee;
  color: #999;
  font-style: italic;
  opacity: 1;
  border-color: #ced4da;
  cursor: not-allowed;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
 
}
.spinner-border {
  color: #ff69b4;
}
</style>